import { useState, useEffect } from 'react';
import { getLocationData, getWeatherCityName } from './locationMappings';

interface WeatherData {
  temperature: number;
  condition: string;
  icon: string;
  loading: boolean;
  error: string | null;
}

interface ExchangeRateData {
  rate: number;
  fromCurrency: string;
  toCurrency: string;
  loading: boolean;
  error: string | null;
}

interface LocalTimeData {
  time: string;
  date: string;
  timezone: string;
}

interface DashboardData {
  weather: WeatherData;
  exchangeRate: ExchangeRateData;
  localTime: LocalTimeData;
}

/**
 * 获取天气数据（使用 Open-Meteo 免费 API）
 */
async function fetchWeather(location: string): Promise<Omit<WeatherData, 'loading' | 'error'>> {
  const cityName = getWeatherCityName(location);
  const locationData = getLocationData(location);
  
  try {
    // 1. 先通过 geocoding API 获取坐标
    const geoResponse = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(cityName)}&count=1&language=zh&format=json`
    );
    
    if (!geoResponse.ok) {
      throw new Error('Failed to fetch location coordinates');
    }
    
    const geoData = await geoResponse.json();
    
    if (!geoData.results || geoData.results.length === 0) {
      throw new Error('Location not found');
    }
    
    const { latitude, longitude } = geoData.results[0];
    
    // 2. 使用坐标获取天气
    const weatherResponse = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&timezone=auto`
    );
    
    if (!weatherResponse.ok) {
      throw new Error('Failed to fetch weather data');
    }
    
    const weatherData = await weatherResponse.json();
    const current = weatherData.current;
    
    // 根据 weather_code 映射天气状况
    const weatherCode = current.weather_code;
    const { condition, icon } = getWeatherCondition(weatherCode);
    
    return {
      temperature: Math.round(current.temperature_2m),
      condition,
      icon,
    };
  } catch (error) {
    console.error('[useDashboardData] Failed to fetch weather:', error);
    throw error;
  }
}

/**
 * 根据 WMO Weather Code 获取天气状况
 * https://open-meteo.com/en/docs
 */
function getWeatherCondition(code: number): { condition: string; icon: string } {
  if (code === 0) return { condition: '晴天', icon: '☀️' };
  if (code <= 3) return { condition: '多云', icon: '⛅' };
  if (code <= 48) return { condition: '雾', icon: '🌫️' };
  if (code <= 67) return { condition: '雨', icon: '🌧️' };
  if (code <= 77) return { condition: '雪', icon: '❄️' };
  if (code <= 82) return { condition: '阵雨', icon: '🌦️' };
  if (code <= 86) return { condition: '阵雪', icon: '🌨️' };
  if (code <= 99) return { condition: '雷暴', icon: '⛈️' };
  return { condition: '未知', icon: '🌡️' };
}

/**
 * 获取汇率数据（使用已有的 currency service 或 Frankfurter API）
 */
async function fetchExchangeRate(fromCurrency: string, toCurrency: string): Promise<number> {
  if (fromCurrency === toCurrency) {
    return 1;
  }
  
  try {
    // 使用 Frankfurter API（免费，无需 API key）
    const response = await fetch(
      `https://api.frankfurter.app/latest?from=${fromCurrency}&to=${toCurrency}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch exchange rate');
    }
    
    const data = await response.json();
    return data.rates[toCurrency];
  } catch (error) {
    console.error('[useDashboardData] Failed to fetch exchange rate:', error);
    throw error;
  }
}

/**
 * 获取当地时间
 */
function getLocalTime(timezone: string): LocalTimeData {
  try {
    const now = new Date();
    const timeFormatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    
    const dateFormatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    return {
      time: timeFormatter.format(now),
      date: dateFormatter.format(now),
      timezone,
    };
  } catch (error) {
    console.error('[useDashboardData] Failed to get local time:', error);
    // Fallback to local time
    const now = new Date();
    return {
      time: now.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false }),
      date: now.toLocaleDateString('zh-CN'),
      timezone: 'Local',
    };
  }
}

/**
 * Dashboard data hook
 */
export function useDashboardData(location: string | null | undefined, baseCurrency: string = 'CNY'): DashboardData {
  const [weather, setWeather] = useState<WeatherData>({
    temperature: 0,
    condition: '',
    icon: '',
    loading: true,
    error: null,
  });
  
  const [exchangeRate, setExchangeRate] = useState<ExchangeRateData>({
    rate: 1,
    fromCurrency: baseCurrency,
    toCurrency: baseCurrency,
    loading: true,
    error: null,
  });
  
  const [localTime, setLocalTime] = useState<LocalTimeData>({
    time: '--:--',
    date: '',
    timezone: '',
  });
  
  // 获取天气数据
  useEffect(() => {
    if (!location) {
      setWeather(prev => ({ ...prev, loading: false, error: '未指定目的地' }));
      return;
    }
    
    let mounted = true;
    
    const loadWeather = async () => {
      try {
        setWeather(prev => ({ ...prev, loading: true, error: null }));
        const data = await fetchWeather(location);
        
        if (mounted) {
          setWeather({
            ...data,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        if (mounted) {
          setWeather(prev => ({
            ...prev,
            loading: false,
            error: '获取天气失败',
          }));
        }
      }
    };
    
    loadWeather();
    
    // 每 30 分钟刷新一次天气
    const interval = setInterval(loadWeather, 30 * 60 * 1000);
    
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [location]);
  
  // 获取汇率数据
  useEffect(() => {
    if (!location) {
      setExchangeRate(prev => ({ ...prev, loading: false, error: '未指定目的地' }));
      return;
    }
    
    const locationData = getLocationData(location);
    const localCurrency = locationData.currency;
    
    if (localCurrency === baseCurrency) {
      setExchangeRate({
        rate: 1,
        fromCurrency: baseCurrency,
        toCurrency: localCurrency,
        loading: false,
        error: null,
      });
      return;
    }
    
    let mounted = true;
    
    const loadExchangeRate = async () => {
      try {
        setExchangeRate(prev => ({ ...prev, loading: true, error: null }));
        const rate = await fetchExchangeRate(baseCurrency, localCurrency);
        
        if (mounted) {
          setExchangeRate({
            rate,
            fromCurrency: baseCurrency,
            toCurrency: localCurrency,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        if (mounted) {
          setExchangeRate(prev => ({
            ...prev,
            loading: false,
            error: '获取汇率失败',
          }));
        }
      }
    };
    
    loadExchangeRate();
    
    // 每 1 小时刷新一次汇率
    const interval = setInterval(loadExchangeRate, 60 * 60 * 1000);
    
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [location, baseCurrency]);
  
  // 更新当地时间
  useEffect(() => {
    if (!location) {
      setLocalTime({
        time: '--:--',
        date: '',
        timezone: '',
      });
      return;
    }
    
    const locationData = getLocationData(location);
    
    const updateTime = () => {
      setLocalTime(getLocalTime(locationData.timezone));
    };
    
    updateTime();
    
    // 每分钟更新一次时间
    const interval = setInterval(updateTime, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [location]);
  
  return {
    weather,
    exchangeRate,
    localTime,
  };
}
